/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd and/or Orbit Tickets Ltd. All rights reserved.
This file contains a combination of:
- Protected Intellectual Property owned by Kaleida Ltd; and
- Custom / Included Intellectual Property owned by the Customer,
and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (being any application not developed by Kaleida), whether or not such application forms part of the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, where separation between Protected Intellectual Property and Custom / Included Intellectual Property is unclear, this file shall be treated as containing Protected Intellectual Property.
*/

// IP-NOTICE: MIXED
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Component } from '@angular/core';
import { AsideService } from 'app/layout/components/aside-container/aside.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationModalComponent } from 'app/shared/components/confirmation-modal/confirmation-modal.component';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { passwordsMustMatch } from 'app/shared/validation/custom-validators';
import { UserResetPasswordService } from './user-reset-password.service';
import { Constants } from 'app/core/models/constants';
import { Router } from '@angular/router';
import { PasswordChangeInvocationSource } from 'app/shared/enumeration/password-change-invocation-source';
import { RegexValidationStrings } from 'app/shared/validation/reg-ex-validation-constants';
import { CookieService } from 'app/shared/services/cookie.service';

@Component({
	selector: 'user-reset-password',
	templateUrl: './user-reset-password.component.html',
	standalone: false
})
export class UserResetPasswordComponent {
	public data: any;

	hidePassword = true;
	dataForm: FormGroup;
	password: AbstractControl;
	confirmPassword: AbstractControl;

	private loggedOnUserId: number;

	constructor(
		fb: FormBuilder,
		private service: UserResetPasswordService,
		private asideService: AsideService,
		private formHelperService: ReactiveFormHelperService,
		private dialog: MatDialog,
		private router: Router,
		private cookieService: CookieService,
	) {
		this.dataForm = fb.group({
			password: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(12), Validators.pattern(RegexValidationStrings.PASSWORD)]],
			confirmPassword: ['', [Validators.required, Validators.maxLength(12), passwordsMustMatch()]],
		});

		this.password = this.dataForm.controls['password'];
		this.confirmPassword = this.dataForm.controls['confirmPassword'];

		this.loggedOnUserId = -1;
		//this.loggedOnUserId = +this.cookieService.getLoggedInUserId();
		this.loggedOnUserId = +this.cookieService.get(Constants.Cookies.KeyLoggedInUserId);
	}

	get resettingOwnPassword(): boolean {
		return this.data.invokedFromUserMenu || this.data.itemId === this.loggedOnUserId;
	}


	save() {
		// specific call to check this one field
		this.confirmPassword.updateValueAndValidity();

		const invocationSource = this.data.invokedFromUserMenu ? PasswordChangeInvocationSource.UserMenu : PasswordChangeInvocationSource.UserList;

		if (this.formHelperService.isFormValid(this.dataForm)) {
			if (this.resettingOwnPassword) {
				this.service.processChangeOwnPassword(this.password.value, invocationSource).then(() => {
					this.asideService.close();

					// sign out
					localStorage.removeItem(Constants.LocalStorage.KeyAccessToken);
					localStorage.removeItem(Constants.LocalStorage.KeyRefreshToken);
					this.cookieService.delete(Constants.Cookies.KeyLoggedInUserId);
					this.cookieService.delete(Constants.Cookies.KeyLoggedInUserName);
					this.cookieService.delete(Constants.Cookies.KeyLoggedInFullName);
					this.cookieService.delete(Constants.Cookies.KeyOrganiserId);
					this.router.navigateByUrl('/login');
				});
			} else {
				this.service.processChangePassword(this.data.itemId, this.password.value, invocationSource).then(() => {
					this.asideService.close();
				});
			}
		}
	}

	cancel() {
		const enableUnsavedChangesWarning = false;
		const isFormDirty = this.formHelperService.isFormDirty(this.dataForm);

		if (enableUnsavedChangesWarning && isFormDirty) {
			const dialogRef = this.dialog.open(ConfirmationModalComponent, {
				width: '450px',
				data: {
					title: 'Confirm Lost Changes',
					message: 'Changes have been made. Closing this form will lose these changes. Are you sure you want to continue?',
				},
			});
			dialogRef.componentInstance.onClose.subscribe((result) => {
				if (result.result) {
					this.asideService.close('cancel');
				}
			});
		} else {
			this.asideService.close('cancel');
		}
	}

	passwordFieldChanged(){
		if(this.confirmPassword.touched) this.confirmPassword.updateValueAndValidity();
	}
}
